import React from "react";
import Box from "@mui/material/Box";
import Link from "@mui/material/Link";
import styled from "styled-components";
import { jt, t } from "ttag";

import { Text } from "merchant-portal-common/Text";
import { isPreviewEnvironment } from "merchant-portal-frontend/src/config";
import breadFavicon from "merchant-portal-frontend/src/images/breadFavicon.png";
import breadPayLoginLogoLight from "merchant-portal-frontend/src/images/login/breadPayLogoLightRegistered.svg";
import rbcLoginLogo from "merchant-portal-frontend/src/images/login/rbcLogo.svg";
import rbcPayplanFavicon from "merchant-portal-frontend/src/images/payplanFavicon.png";
import { BreadLogo } from "merchant-portal-icons/BreadLogo";

import { breadTopLeftLogoText, rbcTopLeftLogoText } from "./colors";

const HeaderLogo = styled.picture`
	margin-right: 4.5rem;
	font-weight: 700;
	display: flex;
	align-items: center;
	font-size: 1.6rem;
`;

interface HeaderLogoProps {
	logo: string;
	alt?: string;
	text?: HeaderLogoTextProps;
	sx?: object;
}

interface HeaderLogoTextProps {
	value: string;
	color: string;
}

const BrandingHeaderLogo = (props: HeaderLogoProps) => {
	const { logo, alt = t`Merchant Portal Logo`, text, sx } = props;
	return (
		<HeaderLogo>
			<img
				data-testid="header-logo-image"
				src={logo}
				alt={alt}
				style={{
					height: "4.25rem",
				}}
			/>
			{text && (
				<Text
					type="h3"
					testId="header-logo-text"
					customOverride={{ color: text.color, ml: 1, ...sx }}
				>
					{text.value}
				</Text>
			)}
		</HeaderLogo>
	);
};

interface LoginLogoProps {
	logo: string;
	alt?: string;
}

enum LogoTestId {
	BrandLogoId = "brandLogoId",
}

const BrandingLoginLogo = (props: LoginLogoProps) => {
	const { logo, alt = t`Merchant Portal Logo` } = props;
	return (
		<img
			src={logo}
			alt={alt}
			data-testid={LogoTestId.BrandLogoId}
			style={{ height: "7rem" }}
		/>
	);
};

interface TitleProps {
	sx?: object;
	title: string;
}

const MerchantPortalTitleNewMerchant = (props: TitleProps) => {
	const { sx, title } = props;
	const styles = {
		...sx,
		color: "common.darkGray",
		fontWeight: 400,
	};
	return (
		<Box sx={styles}>
			<Text type="h1" customOverride={styles}>
				{title}
			</Text>
		</Box>
	);
};

const MerchantPortalTitleLogin = () => {
	const styles = {
		fontSize: "3.4rem",
		color: "common.darkGray",
		margin: "0 0 0.8rem 0",
	};
	const title = isPreviewEnvironment() ? t`Preview Merchant Portal` : t`Merchant Portal`;
	return (
		<Text type="h1" customOverride={styles} testId="merchant-portal-login-header">
			{title}
		</Text>
	);
};

export interface Branding {
	productName: string;
	merchantPortalTitleLogin: JSX.Element;
	merchantPortalTitleNewMerchant: JSX.Element;
	LogoImage?: React.ElementType;
	loginLogo?: JSX.Element;
	headerLogo?: JSX.Element;
	appPageTitle?: string;
	favicon?: string;
	institutionName?: string;
	defaultErrorMessage?: string | string[];
}

const breadEmailLink = (
	<Link key="mail-link" href="mailto:success@breadfinance.com">{t`Merchant Success Team`}</Link>
);

// Bread Pay®-specific branding
export const breadBranding: Branding = {
	productName: t`Bread Pay®`,
	institutionName: t`Bread Financial®`,
	merchantPortalTitleLogin: <MerchantPortalTitleLogin />,
	merchantPortalTitleNewMerchant: <MerchantPortalTitleNewMerchant title={t`Merchant Portal`} />,
	appPageTitle: isPreviewEnvironment()
		? t`Bread Pay® Preview Merchant Portal`
		: t`Bread Pay® Merchant Portal`,
	favicon: breadFavicon,
	LogoImage: BreadLogo,
	headerLogo: (
		<BrandingHeaderLogo
			logo={breadPayLoginLogoLight.toString()}
			alt={t`Bread Pay® Merchant Portal Logo`}
			text={{
				value: isPreviewEnvironment() ? t`Preview Merchant Portal` : t`Merchant Portal`,
				color: breadTopLeftLogoText,
			}}
		/>
	),
	loginLogo: (
		<BrandingLoginLogo
			logo={breadPayLoginLogoLight.toString()}
			alt={t`Bread Pay® Merchant Portal Logo`}
		/>
	),
	defaultErrorMessage: jt`Something went wrong while processing your request. Please contact the ${breadEmailLink} if you keep experiencing this issue.`,
};

const rbcEmailLink = (
	<Link
		key="mail-link"
		href="mailto:payplan@rbc.com"
	>{t`PayPlan by RBC Merchant Support Team`}</Link>
);

// RBC-specific branding
export const RBCBranding: Branding = {
	productName: t`PayPlan by RBC™`, // value from Aksel Fatih & Matt LePoire
	institutionName: t`RBC Ampli Inc.`, // value from Matt LePoire
	merchantPortalTitleLogin: <MerchantPortalTitleLogin />,
	merchantPortalTitleNewMerchant: <MerchantPortalTitleNewMerchant title={t`Merchant Portal`} />,
	headerLogo: (
		<BrandingHeaderLogo
			logo={rbcLoginLogo.toString()}
			text={{
				value: isPreviewEnvironment() ? t`Preview Merchant Portal` : t`Merchant Portal`,
				color: rbcTopLeftLogoText,
			}}
			sx={{ mb: "8px" }}
		/>
	),
	loginLogo: <BrandingLoginLogo logo={rbcLoginLogo.toString()} />,
	appPageTitle: isPreviewEnvironment()
		? t`PayPlan Preview Merchant Portal`
		: t`PayPlan Merchant Portal`,
	favicon: rbcPayplanFavicon,
	defaultErrorMessage: jt`Something went wrong while processing your request. Please contact the ${rbcEmailLink} if you keep experiencing this issue.`,
};
